import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  calculateMonthlyPayment,
  spreadArrearsOverTerm,
  extendLoanTerm,
  reducedPaymentPlan,
  calculateAPR,
  calculateEffectiveAPR,
  convertMonthlyToAnnualRate,
  calculateTotalInterest,
  spreadArrearsOverTermWithDates
} from '../../utils/calculations';
import Decimal from 'decimal.js';
import * as Sentry from '@sentry/browser';

const RepaymentRestructuringCalculator = ({ onCalculate }) => {
  const [isCalculating, setIsCalculating] = useState(false);
  const [showAdvancedDates, setShowAdvancedDates] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      currentBalance: 8000,
      monthlyInterestRate: 0.48,
      remainingMonths: 48,
      arrearsAmount: 500,
      restructureType: 'spread',
      newTermMonths: 60,
      reducedPayment: 150,
      originalPrincipal: 10000,
      originalTermMonths: 60,
      startDate: '',
      currentDate: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
    }
  });

  const restructureType = watch('restructureType');
  const monthlyRate = watch('monthlyInterestRate');

  // Convert monthly interest rate to annual interest rate
  const calculatedAnnualRate = convertMonthlyToAnnualRate(monthlyRate);

  const onSubmit = async (data) => {
    try {
      setIsCalculating(true);
      console.log('Calculating repayment restructuring with:', data);
      
      // Parse form values and convert to Decimal objects for calculations
      const currentBalance = new Decimal(data.currentBalance);
      const monthlyInterestRate = new Decimal(data.monthlyInterestRate);
      const annualInterestRate = new Decimal(convertMonthlyToAnnualRate(data.monthlyInterestRate));
      const remainingMonths = parseInt(data.remainingMonths, 10);
      const arrearsAmount = new Decimal(data.arrearsAmount || 0);
      const restructureType = data.restructureType;
      
      let results;
      
      // Calculate original monthly payment
      const originalMonthlyPayment = calculateMonthlyPayment(currentBalance, annualInterestRate, remainingMonths);
      
      // Calculate standard APR (before restructuring)
      const apr = calculateAPR(currentBalance, annualInterestRate, remainingMonths);
      
      // Apply different restructuring methods based on selection
      if (restructureType === 'spread') {
        // Spread arrears over remaining term
        const restructureResults = data.startDate && data.currentDate 
          ? spreadArrearsOverTermWithDates(
              currentBalance,
              annualInterestRate,
              remainingMonths,
              arrearsAmount,
              data.originalPrincipal || null,
              data.originalTermMonths || null,
              data.startDate,
              data.currentDate
            )
          : spreadArrearsOverTerm(
              currentBalance,
              annualInterestRate,
              remainingMonths,
              arrearsAmount,
              data.originalPrincipal || null,
              data.originalTermMonths || null
            );
        
        // Calculate effective APR after restructuring
        const totalRepaid = restructureResults.newMonthlyPayment.times(remainingMonths);
        const effectiveAPR = calculateEffectiveAPR(
          currentBalance,
          totalRepaid,
          remainingMonths
        );
        
        // Calculate total interest from the schedule
        const totalInterest = restructureResults.schedule.reduce((sum, payment) => {
          return sum.plus(new Decimal(payment.interest));
        }, new Decimal(0));
        
        // Calculate original total interest and interest increase
        const originalTotalInterest = calculateTotalInterest(currentBalance, annualInterestRate, remainingMonths);
        const additionalInterest = totalInterest.minus(originalTotalInterest);
        
        // Calculate original total cost vs restructured total cost
        const originalTotalCost = originalMonthlyPayment.times(remainingMonths);
        const newTotalCost = restructureResults.newMonthlyPayment.times(remainingMonths);
        const additionalCost = newTotalCost.minus(originalTotalCost);
        
        results = {
          calculatorType: 'restructure',
          restructureType: 'spread',
          currentBalance: currentBalance.toNumber(),
          monthlyInterestRate: monthlyInterestRate.toNumber(),
          annualInterestRate: annualInterestRate.toNumber(),
          apr: apr.toNumber(),
          effectiveAPR: effectiveAPR.toNumber(),
          remainingMonths,
          arrearsAmount: arrearsAmount.toNumber(),
          originalPrincipal: restructureResults.originalPrincipal.toNumber(),
          originalTermMonths: restructureResults.originalTermMonths,
          originalMonthlyPayment: originalMonthlyPayment.toNumber(),
          currentPaymentWithoutArrears: restructureResults.currentPaymentWithoutArrears.toNumber(),
          newMonthlyPayment: restructureResults.newMonthlyPayment.toNumber(),
          increasedMonthlyAmount: restructureResults.increasedMonthlyAmount.toNumber(),
          percentageIncrease: restructureResults.percentageIncrease.toNumber(),
          originalTotalCost: originalTotalCost.toNumber(),
          newTotalCost: newTotalCost.toNumber(),
          additionalCost: additionalCost.toNumber(),
          originalTotalInterest: originalTotalInterest.toNumber(),
          additionalInterest: additionalInterest.toNumber(),
          totalInterest: totalInterest.toNumber(),
          dateBasedCalculation: restructureResults.dateBasedCalculation || null,
          schedule: restructureResults.schedule.map(payment => ({
            ...payment,
            payment: payment.payment.toNumber(),
            principal: payment.principal.toNumber(),
            interest: payment.interest.toNumber(),
            totalInterest: payment.totalInterest.toNumber(),
            balance: payment.balance.toNumber(),
            remainingToRepay: payment.remainingToRepay.toNumber()
          }))
        };
      } 
      else if (restructureType === 'extend') {
        // Extend loan term
        const newTermMonths = parseInt(data.newTermMonths, 10);
        
        // Validate new term
        if (newTermMonths <= remainingMonths) {
          throw new Error('New term must be longer than the remaining term');
        }
        
        // Calculate extended term results
        const restructureResults = extendLoanTerm(
          currentBalance,
          annualInterestRate,
          newTermMonths,
          remainingMonths,
          arrearsAmount,
          data.originalPrincipal || null,
          data.originalTermMonths || null
        );
        
        if (restructureResults.error) {
          throw new Error(restructureResults.message);
        }
        
        // Calculate effective APR after restructuring
        const totalRepaid = restructureResults.newMonthlyPayment.times(newTermMonths);
        const effectiveAPR = calculateEffectiveAPR(
          currentBalance.plus(arrearsAmount),
          totalRepaid,
          newTermMonths
        );
        
        // Calculate original total interest and interest increase
        const originalTotalInterest = calculateTotalInterest(currentBalance, annualInterestRate, remainingMonths);
        const additionalInterest = restructureResults.totalInterest.minus(originalTotalInterest);
        
        // Calculate original total cost vs restructured total cost
        const originalTotalCost = originalMonthlyPayment.times(remainingMonths);
        const newTotalCost = restructureResults.newMonthlyPayment.times(newTermMonths);
        const additionalCost = newTotalCost.minus(originalTotalCost);
        
        results = {
          calculatorType: 'restructure',
          restructureType: 'extend',
          currentBalance: currentBalance.toNumber(),
          monthlyInterestRate: monthlyInterestRate.toNumber(),
          annualInterestRate: annualInterestRate.toNumber(),
          apr: apr.toNumber(),
          effectiveAPR: effectiveAPR.toNumber(),
          arrearsAmount: arrearsAmount.toNumber(),
          originalPrincipal: restructureResults.originalPrincipal.toNumber(),
          originalTermMonths: restructureResults.originalTermMonths,
          originalTermRemaining: remainingMonths,
          newTermMonths: newTermMonths,
          additionalMonths: newTermMonths - remainingMonths,
          originalMonthlyPayment: restructureResults.originalMonthlyPayment.toNumber(),
          newMonthlyPayment: restructureResults.newMonthlyPayment.toNumber(),
          originalTotalCost: originalTotalCost.toNumber(),
          newTotalCost: newTotalCost.toNumber(),
          additionalCost: additionalCost.toNumber(),
          originalTotalInterest: originalTotalInterest.toNumber(),
          additionalInterest: additionalInterest.toNumber(),
          totalInterest: restructureResults.totalInterest.toNumber(),
          schedule: restructureResults.schedule.map(payment => ({
            ...payment,
            payment: payment.payment.toNumber(),
            principal: payment.principal.toNumber(),
            interest: payment.interest.toNumber(),
            totalInterest: payment.totalInterest.toNumber(),
            balance: payment.balance.toNumber(),
            remainingToRepay: payment.remainingToRepay.toNumber()
          }))
        };
      } 
      else if (restructureType === 'reduce') {
        // Reduce monthly payment
        const reducedPayment = new Decimal(data.reducedPayment);
        
        // Validate reduced payment
        if (reducedPayment.greaterThanOrEqualTo(originalMonthlyPayment)) {
          throw new Error('Reduced payment must be less than the current payment');
        }
        
        // Calculate reduced payment plan
        const restructureResults = reducedPaymentPlan(
          currentBalance,
          annualInterestRate,
          reducedPayment,
          arrearsAmount
        );
        
        if (restructureResults.error) {
          throw new Error(restructureResults.message);
        }
        
        // Calculate effective APR after restructuring
        const totalRepaid = reducedPayment.times(restructureResults.newTermMonths);
        const effectiveAPR = calculateEffectiveAPR(
          currentBalance.plus(arrearsAmount),
          totalRepaid,
          restructureResults.newTermMonths
        );
        
        // Calculate original total interest and interest increase
        const originalTotalInterest = calculateTotalInterest(currentBalance, annualInterestRate, remainingMonths);
        const additionalInterest = restructureResults.totalInterest.minus(originalTotalInterest);
        
        // Calculate original total cost vs restructured total cost
        const originalTotalCost = originalMonthlyPayment.times(remainingMonths);
        const newTotalCost = reducedPayment.times(restructureResults.newTermMonths);
        const additionalCost = newTotalCost.minus(originalTotalCost);
        
        // Calculate payment reduction correctly based on original terms
        const paymentReduction = originalMonthlyPayment.minus(reducedPayment);
        const percentageReduction = paymentReduction.div(originalMonthlyPayment).times(100);
        
        results = {
          calculatorType: 'restructure',
          restructureType: 'reduce',
          currentBalance: currentBalance.toNumber(),
          monthlyInterestRate: monthlyInterestRate.toNumber(),
          annualInterestRate: annualInterestRate.toNumber(),
          apr: apr.toNumber(),
          effectiveAPR: effectiveAPR.toNumber(),
          arrearsAmount: arrearsAmount.toNumber(),
          originalTermRemaining: remainingMonths,
          newTermMonths: restructureResults.newTermMonths.toNumber(),
          additionalMonths: restructureResults.newTermMonths.toNumber() - remainingMonths,
          originalMonthlyPayment: originalMonthlyPayment.toNumber(),
          newMonthlyPayment: reducedPayment.toNumber(),
          paymentReduction: paymentReduction.toNumber(),
          percentageReduction: percentageReduction.toNumber(),
          originalTotalCost: originalTotalCost.toNumber(),
          newTotalCost: newTotalCost.toNumber(),
          additionalCost: additionalCost.toNumber(),
          originalTotalInterest: originalTotalInterest.toNumber(),
          additionalInterest: additionalInterest.toNumber(),
          totalInterest: restructureResults.totalInterest.toNumber(),
          schedule: restructureResults.schedule.map(payment => ({
            ...payment,
            payment: payment.payment.toNumber(),
            principal: payment.principal.toNumber(),
            interest: payment.interest.toNumber(),
            totalInterest: payment.totalInterest.toNumber(),
            balance: payment.balance.toNumber(),
            remainingToRepay: payment.remainingToRepay.toNumber()
          }))
        };
      }
      
      console.log('Restructuring calculation results:', results);
      
      // Pass results to parent component
      onCalculate(results);
    } catch (error) {
      console.error('Error calculating repayment restructuring:', error);
      
      // Capture the error in Sentry with additional context
      Sentry.captureException(error, {
        tags: {
          component: 'RepaymentRestructuringCalculator',
          calculatorType: 'restructure'
        },
        extra: {
          formData: data,
          restructureType: data.restructureType,
          arrearsAmount: data.arrearsAmount,
          currentBalance: data.currentBalance,
          errorMessage: error.message,
          errorStack: error.stack
        }
      });
      
      alert(`Error: ${error.message || 'An error occurred during calculation. Please check your input values.'}`);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="card">
      <h2 className="section-title">Repayment Restructuring Calculator</h2>
      <p className="mb-4 text-gray-600">
        Simulate different ways to restructure your loan repayments. This can be used proactively when anticipating financial difficulties or to manage existing arrears.
      </p>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Outstanding Balance (£)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="0.01"
              {...register("currentBalance", { 
                required: "Current balance is required", 
                min: { value: 1, message: "Amount must be positive" },
                valueAsNumber: true
              })}
            />
            {errors.currentBalance && (
              <p className="mt-1 text-sm text-red-600">{errors.currentBalance.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monthly Interest Rate (%)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="0"
              step="0.01"
              {...register("monthlyInterestRate", { 
                required: "Monthly interest rate is required",
                min: { value: 0, message: "Rate must be 0 or positive" },
                valueAsNumber: true
              })}
            />
            {errors.monthlyInterestRate && (
              <p className="mt-1 text-sm text-red-600">{errors.monthlyInterestRate.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Annual equivalent: {calculatedAnnualRate.toFixed(2)}%
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remaining Loan Term (months)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="1"
              {...register("remainingMonths", { 
                required: "Remaining term is required",
                min: { value: 1, message: "Term must be at least 1 month" },
                valueAsNumber: true
              })}
            />
            {errors.remainingMonths && (
              <p className="mt-1 text-sm text-red-600">{errors.remainingMonths.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount in Arrears (£)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="0"
              step="0.01"
              {...register("arrearsAmount", { 
                min: { value: 0, message: "Amount must be 0 or positive" },
                valueAsNumber: true
              })}
            />
            {errors.arrearsAmount && (
              <p className="mt-1 text-sm text-red-600">{errors.arrearsAmount.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Leave as 0 if planning ahead for potential financial difficulties. Enter actual arrears amount if already behind on payments.
            </p>
          </div>

          <div className="col-span-2">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
              <div className="flex">
                <div className="shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>For accurate calculations when mid-way through your loan:</strong> If you've already made payments on your loan, please enter your original loan terms below. This ensures the payment comparisons are accurate.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Original Loan Amount (£)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="0.01"
              {...register("originalPrincipal", { 
                min: { value: 1, message: "Amount must be positive" },
                valueAsNumber: true
              })}
            />
            {errors.originalPrincipal && (
              <p className="mt-1 text-sm text-red-600">{errors.originalPrincipal.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              The original loan amount when you first took the loan. Leave blank if this is a new loan or if you want to use current balance.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Original Loan Term (months)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="1"
              {...register("originalTermMonths", { 
                min: { value: 1, message: "Term must be at least 1 month" },
                valueAsNumber: true
              })}
            />
            {errors.originalTermMonths && (
              <p className="mt-1 text-sm text-red-600">{errors.originalTermMonths.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              The original term when you first took the loan. Leave blank if this is a new loan or if you want to use remaining term.
            </p>
          </div>

          <div className="col-span-2">
            <div className="border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={() => setShowAdvancedDates(!showAdvancedDates)}
                className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>Enhanced Date-Based Calculations (Optional)</span>
                <svg 
                  className={`ml-2 h-4 w-4 transform transition-transform ${showAdvancedDates ? 'rotate-180' : ''}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <p className="mt-1 text-xs text-gray-500">
                For maximum precision when payment timing matters (e.g., mid-payment period restructuring, irregular payment dates)
              </p>
            </div>
          </div>

          {showAdvancedDates && (
            <>
              <div className="col-span-2">
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4">
                  <div className="flex">
                    <div className="shrink-0">
                      <svg className="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-amber-700">
                        <strong>Enhanced Precision Mode:</strong> Provide your original loan start date for calculations that account for exact days and payment timing. Most users won't need this level of precision.
                      </p>
                      <ul className="mt-2 text-sm text-amber-700 list-disc list-inside">
                        <li><strong>Use when:</strong> Restructuring mid-payment period, irregular payment dates, or compliance requirements</li>
                        <li><strong>Skip when:</strong> Standard monthly payments on consistent dates (the basic calculation is sufficient)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Original Loan Start Date
                </label>
                <input
                  type="date"
                  className="form-input box-border"
                  {...register("startDate")}
                />
                <p className="mt-1 text-xs text-gray-500">
                  The exact date when your original loan started and first payment was due.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Date
                </label>
                <input
                  type="date"
                  className="form-input box-border"
                  {...register("currentDate")}
                />
                <p className="mt-1 text-xs text-gray-500">
                  The date for which you want to calculate the restructuring (defaults to today).
                </p>
              </div>
            </>
          )}
          
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Restructuring Method
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative border rounded-md px-4 py-3 flex items-center cursor-pointer">
                <input
                  id="restructure-spread"
                  type="radio"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  value="spread"
                  {...register("restructureType")}
                />
                <label htmlFor="restructure-spread" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                  Spread Arrears
                </label>
              </div>
              
              <div className="relative border rounded-md px-4 py-3 flex items-center cursor-pointer">
                <input
                  id="restructure-extend"
                  type="radio"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  value="extend"
                  {...register("restructureType")}
                />
                <label htmlFor="restructure-extend" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                  Extend Term
                </label>
              </div>
              
              <div className="relative border rounded-md px-4 py-3 flex items-center cursor-pointer">
                <input
                  id="restructure-reduce"
                  type="radio"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  value="reduce"
                  {...register("restructureType")}
                />
                <label htmlFor="restructure-reduce" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                  Reduce Payment
                </label>
              </div>
            </div>
          </div>
          
          {restructureType === 'extend' && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Loan Term (months)
              </label>
              <input
                type="number"
                className="form-input box-border"
                min="1"
                step="1"
                {...register("newTermMonths", { 
                  required: "New term is required",
                  min: { value: 1, message: "Term must be at least 1 month" },
                  valueAsNumber: true
                })}
              />
              {errors.newTermMonths && (
                <p className="mt-1 text-sm text-red-600">{errors.newTermMonths.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                New term must be longer than the remaining term to effectively extend the loan.
              </p>
            </div>
          )}
          
          {restructureType === 'reduce' && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reduced Monthly Payment (£)
              </label>
              <input
                type="number"
                className="form-input box-border"
                min="1"
                step="0.01"
                {...register("reducedPayment", { 
                  required: "Reduced payment amount is required",
                  min: { value: 1, message: "Amount must be positive" },
                  valueAsNumber: true
                })}
              />
              {errors.reducedPayment && (
                <p className="mt-1 text-sm text-red-600">{errors.reducedPayment.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                The reduced payment must be enough to cover at least the interest to eventually pay off the loan.
              </p>
            </div>
          )}
        </div>
        
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
          <div className="flex">
            <div className="shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <strong>Restructuring options explained:</strong>
              </p>
              <ul className="mt-1 text-sm text-green-700 list-disc list-inside">
                <li><strong>Spread Arrears:</strong> Adds missed payments to your balance and recalculates monthly payments over the remaining term. Use with 0 arrears to see current loan structure.</li>
                <li><strong>Extend Term:</strong> Keeps your current payment amount but extends the loan term to accommodate arrears or reduce financial pressure.</li>
                <li><strong>Reduce Payment:</strong> Lowers your monthly payment but extends the term to ensure the loan is paid off.</li>
              </ul>
              <p className="mt-2 text-sm text-green-700">
                <strong>Note:</strong> You can use this calculator proactively (with 0 arrears) to explore restructuring options before facing financial difficulties.
              </p>
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <button 
            type="submit" 
            className="btn btn-primary cursor-pointer"
            disabled={isCalculating}
          >
            {isCalculating ? 'Calculating...' : 'Calculate'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RepaymentRestructuringCalculator;